import React from 'react';
import { Table, TableBody } from '../ui/table';
import DataTableLoader from './DataTableLoader';
import DataTableError from './DataTableError';
import DataTableEmptyState from './DataTableEmptyState';
import DataTablePagination from './DataTablePagination';
import DataTableHeader, { HeaderDefinition } from './DataTableHeader';
import DataTableRow from './DataTableRow';

interface DataTableProps<T> {
  searchData: T[] | null;
  numberOfResults: number;
  resultsPerPage: number;
  isLoading: boolean;
  setResultsPerPage: React.Dispatch<React.SetStateAction<number>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  tableHeaders: HeaderDefinition[];
  handleRowClick?: (item: T) => void;
  hasError: boolean;
  handleFetch: (signal: AbortSignal) => void;
  noDataTitle: string;
  noDataMessage: string | React.ReactNode;
  isClickable?: boolean;
  hideTopPagination?: boolean;
  renderRow: (item: T) => React.ReactNode;
  sortColumn?: string;
  setSortColumn?: React.Dispatch<React.SetStateAction<string>>;
  sortDirection?: 'ASC' | 'DESC';
  setSortDirection?: React.Dispatch<React.SetStateAction<'ASC' | 'DESC'>>;
  onSortChange?: () => void;
  emptyStateAction?: React.ReactNode;
  hasActiveSearch?: boolean;
  noSearchResultsTitle?: string;
  noSearchResultsMessage?: string | React.ReactNode;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  isDragOver?: boolean;
  isDropZone?: boolean;
  tableMinWidth?: number;
}

function DataTable<T>({
  searchData,
  numberOfResults,
  resultsPerPage,
  isLoading,
  setResultsPerPage,
  setPage,
  page,
  tableHeaders,
  handleRowClick,
  hasError,
  handleFetch,
  noDataTitle,
  noDataMessage,
  isClickable = true,
  hideTopPagination = false,
  renderRow,
  sortColumn,
  setSortColumn,
  sortDirection = 'ASC',
  setSortDirection,
  onSortChange,
  emptyStateAction,
  hasActiveSearch = false,
  noSearchResultsTitle,
  noSearchResultsMessage,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver = false,
  isDropZone = false,
  tableMinWidth = 800
}: DataTableProps<T>): React.ReactElement {
  const hasData = searchData && searchData.length > 0;
  const tableIsLoading = isLoading || searchData == null;

  // Track previous search state to prevent flicker
  const [wasSearching, setWasSearching] = React.useState(false);

  React.useEffect(() => {
    if (hasActiveSearch) {
      setWasSearching(true);
    } else if (!hasActiveSearch && !isLoading) {
      // Only clear wasSearching after loading is complete
      setWasSearching(false);
    }
  }, [hasActiveSearch, isLoading]);

  const displayPage = page + 1;

  const handlePageChange = React.useCallback(
    (newPage: React.SetStateAction<number>) => {
      if (typeof newPage === 'function') {
        setPage((prev) => newPage(prev + 1) - 1);
      } else {
        setPage(newPage - 1);
      }
    },
    [setPage]
  );

  return (
    <div className='flex flex-col max-w-full'>
      <DataTableLoader loading={tableIsLoading} />
      {hasError && (
        <DataTableError error='We were unable to retrieve your data. Please try again.' />
      )}
      {!tableIsLoading && !hasError && hasData && (
        <div className='overflow-x-auto'>
          <section
            className={`rounded-md border transition-colors duration-200 w-fit min-w-full ${isDragOver ? 'bg-blue-50' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            aria-label='Data table with drag and drop support'
          >
            <Table className='table-fixed' style={{ minWidth: tableMinWidth }}>
              {setSortColumn && setSortDirection && (
                <DataTableHeader
                  headers={tableHeaders}
                  sortColumn={sortColumn || tableHeaders[0].key}
                  setSortColumn={setSortColumn}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                  onSortChange={onSortChange || (() => {})}
                />
              )}
              <TableBody>
                {searchData?.map((item, idx) => (
                  <DataTableRow
                    key={(item as any).id ?? idx}
                    onClick={
                      handleRowClick
                        ? (): void => handleRowClick(item)
                        : undefined
                    }
                  >
                    {renderRow(item)}
                  </DataTableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        </div>
      )}

      {!tableIsLoading && !hasError && !hasData && searchData !== undefined && (
        <DataTableEmptyState
          title={
            (hasActiveSearch || wasSearching) && noSearchResultsTitle
              ? noSearchResultsTitle
              : noDataTitle
          }
          message={
            (hasActiveSearch || wasSearching) && noSearchResultsMessage
              ? noSearchResultsMessage
              : noDataMessage
          }
          action={
            !(hasActiveSearch || wasSearching) ? emptyStateAction : undefined
          }
          isDragOver={isDragOver}
          isDropZone={isDropZone}
        />
      )}
      {hasData && (
        <DataTablePagination
          totalItems={numberOfResults}
          itemsPerPage={resultsPerPage}
          currentPage={displayPage}
          setCurrentPage={handlePageChange}
          resultsPerPage={resultsPerPage}
          setResultsPerPage={setResultsPerPage}
        />
      )}
    </div>
  );
}

export default DataTable;
